import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Container,
  InputGroup,
  InputGroupText,
  ModalHeader,
  ModalBody,
  Modal,
  ModalFooter,
  Input,
} from "reactstrap";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";
import PaymentReportService from "../../../services/OrderingServices/PaymentReportService";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { formatDate } from "../../../Components/Common/FormatDate";
import CountUp from "react-countup";
import toast from "react-hot-toast";
import { exportToExcel } from "../../../utilis/exportToExcel";
import CardPaymentService from "../../../services/OrderingServices/CardPaymentService";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";

// Helper to format date as YYYY-MM-DD
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const CardPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [paymentTypeData, setPaymentTypeData] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [voidModal, setVoidModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  console.log("🚀 ~ CardPayment ~ selectedItem:", selectedItem);
  const toggleVoidModal = (item) => {
    setSelectedItem(item);
    setVoidModal(!voidModal);
  };
  // FIX: Added missing fields
  const [filters, setFilters] = useState({
    paymentTypeId: location.state?.paymentTypeId || null,
    CardUid: location.state?.CardUid || "",
    userId: location.state?.userId || "",
    type: location.state?.type || "",
    status: location.state?.status || "",
    startDate: null,
    endDate: null,
  });

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  // Fetch payment types
  const fetchPaymentTypes = async () => {
    try {
      const types = await CustomerServices.getCustomerList();
      setPaymentTypeData(types || []);
    } catch (error) {
      console.error("Error fetching payment types:", error);
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    const startDate = filters.startDate
      ? formatLocalDate(filters.startDate)
      : null;
    const endDate = filters.endDate ? formatLocalDate(filters.endDate) : null;

    try {
      const data = await CardPaymentService.cardTxns({
        CardUid: filters.CardUid,
        userId: filters.userId,
        type: filters.type,
        status: filters.status,
        from: startDate,
        to: endDate,
        pageNumber: currentPage,
        pageSize: pageSize,
      });

      setTransactions(data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    }
  };

  // Initial setup: 1 month default filter
  useEffect(() => {
    fetchPaymentTypes();

    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    setFilters((prev) => ({
      ...prev,
      startDate: oneMonthAgo,
      endDate: today,
    }));
  }, []);

  // Fetch data when filters, page, pageSize change
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchTransactions();
    }
  }, [
    filters.paymentTypeId,
    filters.startDate,
    filters.endDate,
    filters.CardUid,
    filters.userId,
    filters.type,
    filters.status,
    currentPage,
    pageSize,
  ]);

  const handleFilterChange = (value, key) => {
    setCurrentPage(1); // reset pagination
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage); // FIXED
    setCurrentPage(page);
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "75px",
    },
    {
      name: "Txn Ref",
      selector: (row) => row.transactionRef,
      cell: (row) => (
        <span
          title={row.transactionRef}
          className="text-truncate cursor-pointer"
        >
          {row.transactionRef}
        </span>
      ),
      width: "15vw",
    },
    {
      name: "Kiosk",
      selector: (row) => row.kioskId,
      cell: (row) => <span>{row.kioskId}</span>,
      width: "12vw",
    },
    {
      name: "Type",
      selector: (row) => row.type,
      cell: (row) => <span>{row.type}</span>,
      width: "12vw",
    },
    {
      name: "Date",
      selector: (row) => row.createdAt,
      cell: (row) => formatDate(row.createdAt),
      width: "12vw",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      cell: (row) => (
        <div className="text-end">
          <NumberWithCommas number={row.amount} />
        </div>
      ),
      width: "10vw",
      right: true,
    },
    {
      name: "Balance Before",
      selector: (row) => row.balanceBefore,
      cell: (row) => (
        <div className="text-end">
          <NumberWithCommas number={row.balanceBefore} />
        </div>
      ),
      width: "10vw",
      right: true,
    },
    {
      name: "Balance After",
      selector: (row) => row.balanceAfter,
      cell: (row) => (
        <div className="text-end">
          <NumberWithCommas number={row.balanceAfter} />
        </div>
      ),
      width: "10vw",
      right: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <span
          className={`badge text-sm ${
            row.status === "Completed"
              ? "bg-success"
              : row.status === "Pending"
              ? "bg-warning"
              : "bg-danger"
          }`}
        >
          {row.status}
        </span>
      ),
      width: "8vw",
      center: true,
    },
    {
      name: "Transaction By",
      selector: (row) => row.createdBy,
      width: "12vw",
      center: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <div>
          <Button
            className="btn btn-soft-danger btn-sm ms-1"
            color="danger"
            onClick={() => toggleVoidModal(row)}
            title="Void Payment"
          >
            <i className="ri-delete-bin-5-line" />
          </Button>
        </div>
      ),
      center: true,
    },
  ];

  const handleVoidPayment = async () => {
    try {
      const response = await CardPaymentService.voidPayment(selectedItem.id, {
        reason: reason,
      });
      if (response) {
        toast.success("Card payment voided successfully");
        setVoidModal(false);
        fetchTransactions();
      }
    } catch (error) {
      toast.error("Failed to void card payment");
    }
  };
  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Card Payment"
          pageTitle="Reports"
          breadcrumbItems={breadcrumbItems}
        />

        <div
          className="p-3 p-lg-4 mt-1"
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <Row className="mb-3">
            <Col lg={3}>
              <h5 className="mb-1 fw-bold text-dark">
                Card Payment Report{" "}
                {filters.userId && paymentTypeData.length > 0
                  ? `(${
                      paymentTypeData.find((pt) => pt.id === filters.userId)
                        ?.firstName
                    })`
                  : ""}
              </h5>
            </Col>
            <Col lg={9}>
              <Row>
                <Col md={5}>
                  <Select
                    options={paymentTypeData.map((pt) => ({
                      value: pt.id,
                      label: `${pt.firstName} ${pt.lastName}`,
                    }))}
                    value={
                      paymentTypeData.find((pt) => pt.id === filters.userId)
                        ? {
                            value: filters.userId,
                            label: paymentTypeData.find(
                              (pt) => pt.id === filters.userId,
                            ).firstName,
                          }
                        : null
                    }
                    onChange={(option) =>
                      handleFilterChange(option?.value || null, "userId")
                    }
                    placeholder="Select Customer"
                    isClearable
                  />
                </Col>
                <Col md={5}>
                  <InputGroup>
                    <Flatpickr
                      className="form-control"
                      value={[filters.startDate, filters.endDate].filter(
                        Boolean,
                      )}
                      onChange={([start, end]) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: start || null,
                          endDate: end || null,
                        }))
                      }
                      options={{
                        mode: "range",
                        dateFormat: "Y-m-d",
                        allowInput: false,
                      }}
                      placeholder="Select Date Range"
                    />
                    <InputGroupText style={{ cursor: "pointer" }}>
                      <i className="bx bx-calendar"></i>
                    </InputGroupText>
                  </InputGroup>
                </Col>
                {/* <Col md={2}>
                  <Button color="success" onClick={handleExport}>
                    <i className="bx bx-export me-1"></i>Export
                  </Button>
                </Col> */}
              </Row>
            </Col>
          </Row>

          <hr style={{ borderColor: "#000000ff", marginTop: "0rem" }} />

          <CustomDataTable
            columns={columns}
            data={transactions}
            noHeader
            pagination={transactions.length > 0}
            striped
            highlightOnHover
            responsive
            persistTableHead
            // paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationDefaultPage={currentPage}
            paginationPerPage={pageSize}
            noDataComponent={
              <div className="text-center my-5 w-100">
                <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                <h5 className="fw-semibold text-secondary">
                  No data Available
                </h5>
                <p className="text-muted mb-0">
                  {filters.paymentTypeId
                    ? "No transactions found for the selected payment type and date range."
                    : "Please choose a payment type to see updated availability."}
                </p>
              </div>
            }
          />
        </div>
      </div>

      <Modal isOpen={voidModal} toggle={toggleVoidModal}>
        <ModalHeader>
          <p>Void Card Payment</p>
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to void this card payment?</p>
          <Input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setVoidModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleVoidPayment}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default CardPayment;
